/* verqo — Buyer Solutions page content (10 pages · shipped live Feb 2026)
   4 personas (retailers / chains / marketplaces / procurement) + 6 industries.
*/
import {
  Store, Building2, Network, ShoppingBag,
  Sparkles, Shirt, UtensilsCrossed, Home, Heart, ShoppingBasket,
  Wallet, TrendingUp, ShieldCheck, Layers, Bot, Users2, Lock, Rocket,
  Landmark, Truck, DollarSign, Bell,
  Briefcase, LineChart, Boxes as BoxesIcon, PieChart, MessageSquare, Search, Clock, Package,
} from "lucide-react";
import { BrowserFrame } from "../../../../components/marketing/SubPageBlocks";

const CTA_PRIMARY = { label: "Request invite", href: "/waitlist?as=buyer" };

const SHOWCASE_CTA = {
  primary:   { label: "Request invite",   href: "/waitlist?as=buyer" },
  secondary: { label: "See how it works", href: "/company/how-it-works" },
};

const commonCta = (title) => ({
  title: title || "One login. Every supplier. Net-30 in seconds.",
  subtitle: "Verqo Buyer is invite-only during launch. Join the list — we'll get you set up in under 48 hours.",
  primary: CTA_PRIMARY,
  secondary: { label: "Talk to us", href: "/company/contact" },
});

const REL = (slug, label, desc) => ({ href: `/buyers/solutions/${slug}`, label, desc });
const RELIND = (slug, label, desc) => ({ href: `/buyers/solutions/industry/${slug}`, label, desc });

const IMG = {
  dashboard:         "/preview-screens/buyer-dashboard/main.jpeg",
  ordersAll:         "/preview-screens/buyer-orders/all.jpeg",
  ordersAction:      "/preview-screens/buyer-orders/action.jpeg",
  paymentsOverview:  "/preview-screens/buyer-payments/overview.jpeg",
  paymentsWallet:    "/preview-screens/buyer-payments/wallet.jpeg",
  paymentsCredit:    "/preview-screens/buyer-payments/credit.jpeg",
  invoices:          "/preview-screens/buyer-invoices/main.jpeg",
  score:             "/preview-screens/buyer-score/main.jpeg",
  shipping:          "/preview-screens/buyer-shipping/main.jpeg",
  suppliers:         "/preview-screens/buyer-suppliers/main.jpeg",
  poInbox:           "/preview-screens/buyer-po-inbox/main.jpeg",
  financing:         "/preview-screens/buyer-financing/main.jpeg",
};

const URL = (path) => `app.verqo.com${path}`;

/* ─────────── PERSONAS ─────────── */

const retailers = {
  eyebrow: "Buyers · Retailers",
  title: "Independent boutiques deserve the same terms the chains get.",
  subtitle: "Verqo levels the playing field — Net-30/60/90 on the same suppliers you already love, one login, one AP process, one Trade Score that follows you.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.dashboard} alt="Buyer Hub — all suppliers in one dashboard" url={URL("/buyer")} />,
  features: {
    heading: "Buying, without the boutique tax.",
    sub: "Every rail an enterprise buyer gets — terms, credit line, trade score, dropship — available to a single-location boutique from day one.",
    items: [
      { icon: Store,       title: "Terms across every supplier",desc: "One line unlocks every Verqo supplier. No re-applying, no re-signing." },
      { icon: Wallet,      title: "AP in 10 minutes/week",       desc: "One statement, one payment. Not forty invoices scattered across email." },
      { icon: TrendingUp,  title: "Build real credit",           desc: "Your on-time payments build a portable Trade Score across the network." },
      { icon: ShieldCheck, title: "Buyer protection",             desc: "Damage, loss, and disputes covered — always." },
      { icon: Bot,         title: "AI reorder",                   desc: "Automated reorder rules for your top-sellers — never run out mid-season." },
      { icon: DollarSign,  title: "Split & extend",              desc: "Break any invoice into 3 or 6 payments or extend to Net-90 — one click, transparent rate." },
    ],
  },
  screenshots: {
    heading: "The retailer-side wholesale-OS.",
    items: [
      { label: "Suppliers",  src: IMG.suppliers,  alt: "Buyer suppliers directory",      url: URL("/buyer/suppliers"), caption: "Suppliers on your network — every one already approved, every one instantly orderable." },
      { label: "Credit",     src: IMG.paymentsCredit, alt: "Buyer credit line",           url: URL("/buyer/payments#credit"), caption: "One credit line, one score, all your suppliers — the boutique-friendly enterprise experience." },
      { label: "Score",      src: IMG.score,      alt: "Buyer trade score dashboard",   url: URL("/buyer/score"),      caption: "Portable Trade Score — grows with every on-time payment across every supplier." },
    ],
    cta: SHOWCASE_CTA,
  },
  related: [
    REL("chains",       "Chains",       "For multi-location retailers."),
    REL("marketplaces","Marketplaces", "For online marketplaces."),
    RELIND("beauty",    "Beauty",       "Beauty-specific buyer flow."),
  ],
  cta: commonCta(),
};

const chains = {
  eyebrow: "Buyers · Chains",
  title: "Multi-location AP without the multi-headache.",
  subtitle: "One master line. Location-level P&L. Verqo powers the AP infrastructure your finance team wants and your buyers actually use.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.paymentsOverview} alt="Multi-location payments overview" url={URL("/buyer/payments")} />,
  features: {
    heading: "AP for the multi-location operator.",
    items: [
      { icon: Building2,  title: "Location-level books",  desc: "Assign every invoice to the right P&L automatically — no manual coding." },
      { icon: Layers,     title: "Approval flows",         desc: "Rules by amount, category, and vendor — approvals routed instantly." },
      { icon: Wallet,     title: "One master line",        desc: "One credit line covers every location. Consolidated reporting for finance." },
      { icon: Users2,     title: "Multi-user roles",       desc: "Store managers, regional buyers, corporate finance — role-based access." },
      { icon: Landmark,   title: "SSO / SAML",             desc: "Okta, Google Workspace, Azure AD — sign in with your existing identity." },
      { icon: Bell,       title: "Exposure alerts",        desc: "Real-time alerts when any location breaches spend or AP thresholds." },
    ],
  },
  screenshots: {
    heading: "Chain AP, in one console.",
    items: [
      { label: "Overview",   src: IMG.paymentsOverview, alt: "Multi-location payments dashboard", url: URL("/buyer/payments"),     caption: "Money in/out consolidated across every location, filterable by store." },
      { label: "PO Inbox",   src: IMG.poInbox,          alt: "PO Inbox across every supplier",     url: URL("/buyer/po-inbox"),      caption: "Every PO — including POs to suppliers off Verqo. One dashboard for AP." },
      { label: "Invoices",   src: IMG.invoices,         alt: "Consolidated invoice inbox",         url: URL("/buyer/invoices"),      caption: "Every invoice across every supplier, aged, filterable, one-click pay." },
    ],
    cta: SHOWCASE_CTA,
  },
  blocks: [
    {
      eyebrow: "For the ops team",
      title: "Cut AP work from 40 hours/week to 4.",
      body: "One Verqo statement replaces forty supplier invoices. Your bookkeeper reconciles once, not forty times.",
      visual: <BrowserFrame src={IMG.paymentsCredit} alt="Master credit line — utilization across locations" url={URL("/buyer/payments#credit")} />,
    },
  ],
  related: [
    REL("retailers",    "Retailers",    "For single-location boutiques."),
    REL("procurement","Procurement", "For institutional teams."),
    RELIND("home",     "Home",         "Home & furniture buying."),
  ],
  cta: commonCta("Buy across locations without buying complexity."),
};

const marketplaces = {
  eyebrow: "Buyers · Marketplaces",
  title: "Wholesale supply for e-com marketplaces.",
  subtitle: "Verqo helps online marketplaces source wholesale supply from the network — with the terms, quality signal, and dropship rails built in.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.suppliers} alt="Suppliers network — programmatic sourcing" url={URL("/buyer/suppliers")} />,
  features: {
    heading: "Built for the way marketplaces actually buy.",
    items: [
      { icon: Network,     title: "Programmatic sourcing", desc: "APIs to shortlist, request, and PO at scale." },
      { icon: Wallet,      title: "Consolidated AP",       desc: "One AP process across every supplier on your platform." },
      { icon: ShieldCheck, title: "Quality signal",         desc: "See how each supplier performs — on-time, quality, dispute rate — before you PO." },
      { icon: Bot,         title: "Auto-reorder",            desc: "Rules-based auto-reorder tied to your marketplace inventory signals." },
    ],
  },
  screenshots: {
    heading: "Programmatic sourcing at scale.",
    items: [
      { label: "Suppliers", src: IMG.suppliers, alt: "Suppliers directory", url: URL("/buyer/suppliers"), caption: "Every supplier — verified, KYB'd, quality-graded — with API access to search and filter." },
      { label: "Orders",    src: IMG.ordersAll, alt: "Orders across every supplier", url: URL("/buyer/orders"), caption: "Every open PO across every supplier — API-first for marketplace integrations." },
    ],
    cta: SHOWCASE_CTA,
  },
  related: [
    REL("retailers","Retailers","Single-location retailers."),
    REL("chains",    "Chains",    "Multi-location chains."),
    REL("procurement","Procurement","Corporate/institutional teams."),
  ],
  cta: commonCta("Bring wholesale supply into your marketplace, without the ops overhead."),
};

const procurement = {
  eyebrow: "Buyers · Procurement",
  title: "Corporate procurement without the RFP theater.",
  subtitle: "For finance and procurement teams buying at institutional scale — SOC 2 controls, punch-out catalogs, approval workflows, and cross-supplier reporting.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.poInbox} alt="Procurement PO Inbox — 230 POs, $194K value" url={URL("/buyer/po-inbox")} />,
  features: {
    heading: "Enterprise controls, consumer UX.",
    items: [
      { icon: ShoppingBag, title: "Punch-out catalogs",   desc: "Integrate with Coupa, SAP Ariba, and internal ERPs." },
      { icon: Layers,      title: "Approval workflows",    desc: "Configurable multi-level approvals with SSO." },
      { icon: Lock,        title: "SOC 2 + SSO",           desc: "SOC 2 Type II, SSO/SAML, audit logs, and DPA on file." },
      { icon: Users2,      title: "Role-based access",     desc: "Requester, approver, finance, admin — granular permissions per user." },
    ],
  },
  screenshots: {
    heading: "Institutional buyer, retail UX.",
    items: [
      { label: "PO Inbox",  src: IMG.poInbox,           alt: "PO inbox — 230 POs, $194K value",   url: URL("/buyer/po-inbox"),       caption: "230 POs, $194K value across 40+ suppliers — some on Verqo, some off. Every PO tracked, audited, and signed." },
      { label: "Payments",  src: IMG.paymentsCredit, alt: "Credit + methods dashboard", url: URL("/buyer/payments#methods"), caption: "Bank-connected via Plaid, SSO ready, audit logs on every payment event." },
    ],
    cta: SHOWCASE_CTA,
  },
  related: [
    REL("chains",       "Chains",       "For multi-location retail chains."),
    REL("marketplaces", "Marketplaces", "For e-com marketplaces."),
    RELIND("cpg",       "CPG",           "CPG procurement flows."),
  ],
  cta: commonCta("Modern procurement, without a nine-month implementation."),
};

/* ─────────── INDUSTRIES ─────────── */

const industryFAQ = (industry) => [
  { q: `Which ${industry} suppliers are on Verqo?`, a: `Verqo has ${industry} suppliers ranging from indie/challenger brands to national multi-line distributors. New suppliers join weekly.` },
  { q: "Do I need to switch banks?",                 a: "No. Verqo works with any US business bank account for ACH pull/push. Card rails also supported." },
];

const INDUSTRY_HERO = {
  beauty:  IMG.suppliers,
  apparel: IMG.ordersAll,
  food:    IMG.invoices,
  home:    IMG.paymentsOverview,
  health:  IMG.score,
  cpg:     IMG.poInbox,
};
const INDUSTRY_HERO_URL = {
  beauty:  URL("/buyer/suppliers"),
  apparel: URL("/buyer/orders"),
  food:    URL("/buyer/invoices"),
  home:    URL("/buyer/payments"),
  health:  URL("/buyer/score"),
  cpg:     URL("/buyer/po-inbox"),
};

const industryPage = (name, icon, slug) => ({
  eyebrow: `Buyers · ${name}`,
  title: `Buy ${name.toLowerCase()} wholesale — the way it should have always worked.`,
  subtitle: `One line, one login, every ${name.toLowerCase()} supplier. Verqo handles credit, payment, and shipping so you can focus on curation.`,
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={INDUSTRY_HERO[slug]} alt={`Verqo — ${name} buyer workflow`} url={INDUSTRY_HERO_URL[slug]} />,
  features: {
    heading: `Built for the way ${name.toLowerCase()} buyers actually shop.`,
    items: [
      { icon: icon,        title: `${name} network`,      desc: `Curated ${name.toLowerCase()} suppliers, verified.` },
      { icon: Wallet,      title: "Net-30/60/90",          desc: "Terms across every supplier, instantly." },
      { icon: TrendingUp,  title: "Portable Trade Score",  desc: "Build reputation across the whole network." },
      { icon: ShieldCheck, title: "Buyer protection",       desc: "Damage, loss, dispute — always covered." },
      { icon: Truck,       title: "Consolidated freight",   desc: "Combine parcels across suppliers when it saves you money." },
      { icon: Bot,         title: "AI reorder",              desc: `Auto-reorder your top ${name.toLowerCase()} sellers based on velocity.` },
    ],
  },
  screenshots: {
    heading: `The ${name.toLowerCase()} buyer flow, one login.`,
    items: [
      { label: "Suppliers",  src: IMG.suppliers,        alt: `${name} suppliers directory`, url: URL("/buyer/suppliers"), caption: `${name} suppliers — verified and instantly orderable.` },
      { label: "Payments",   src: IMG.paymentsOverview, alt: `${name} AP dashboard`,          url: URL("/buyer/payments"),  caption: `Every ${name.toLowerCase()} dollar in and out, one dashboard.` },
    ],
    cta: SHOWCASE_CTA,
  },
  faq: industryFAQ(name.toLowerCase()),
  related: [
    RELIND("beauty",  "Beauty",   "Beauty & personal care."),
    RELIND("apparel", "Apparel",  "Fashion & accessories."),
    RELIND("food",    "Food",     "Food, coffee, beverage."),
  ].filter(r => !r.href.endsWith(slug)),
  cta: commonCta(),
});

const industries = {
  beauty:  industryPage("Beauty",  Sparkles,        "beauty"),
  apparel: industryPage("Apparel", Shirt,           "apparel"),
  food:    industryPage("Food",    UtensilsCrossed, "food"),
  home:    industryPage("Home",    Home,            "home"),
  health:  industryPage("Health",  Heart,           "health"),
  cpg:     industryPage("CPG",     ShoppingBasket,  "cpg"),
};

/* ─────────── BY ROLE (retail-buying-scoped only) ───────────
   Explicitly scoped to the businesses Verqo Buyer serves — independent
   retailers, multi-location chains, e-com marketplaces, and procurement
   teams sourcing in beauty, apparel, food, home, health, and CPG.
   Not built for enterprise procurement in adjacent categories.
*/
const REL_ROLE = (slug, label, desc) => ({ href: `/buyers/solutions/role/${slug}`, label, desc });

const AUDIENCE_LINE = "For teams at retailers, chains, marketplaces, and procurement groups sourcing physical goods across beauty, apparel, food, home, health, and CPG. Not for enterprise IT / SaaS procurement — this is wholesale buying infrastructure.";

const founderOwner = {
  eyebrow: "By role · Founders & Owners of retail shops",
  title: "Run the shop and the buy without the burnout.",
  subtitle: `One login for every supplier, every invoice, every reorder — with Net-30/60/90 preserved across the whole network. ${AUDIENCE_LINE}`,
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.dashboard} alt="Buyer dashboard — everything a retail owner needs" url={URL("/buyer")} />,
  features: {
    heading: "The tools an independent retailer used to only dream about.",
    sub: "Chain buyers have finance teams and PO systems. Verqo brings those tools to the indie shop — on invite-only, no monthly fee.",
    items: [
      { icon: Search,        title: "One search, every brand",       desc: "Discover suppliers across every category — verified, orderable, and instantly priced." },
      { icon: Landmark,      title: "Net-30/60/90 in one click",     desc: "Approved once, use across every supplier in the network — no reapplying per brand." },
      { icon: TrendingUp,    title: "Portable Trade Score",           desc: "Your on-time payment history becomes a portable buyer reputation — better terms follow." },
      { icon: Wallet,        title: "One wallet, one bill",           desc: "ACH, wires, cards on one wallet — see every open invoice and due date at a glance." },
      { icon: Bot,           title: "AI reorder cadence",             desc: "Auto-reorder your top-selling SKUs on the schedule that actually matches your sell-through." },
      { icon: ShieldCheck,   title: "Buyer protection",                desc: "Damage, loss, dispute — always covered. You never eat a bad shipment again." },
    ],
  },
  blocks: [
    {
      eyebrow: "For the person doing everything",
      title: "Reorder, pay, and receive — from your phone, between customers.",
      body: "Verqo Buyer is mobile-first because indie retailers are on the floor, not behind a desk. Reorder in 60 seconds, pay when the invoice hits, track receiving from the back room.",
      visual: <BrowserFrame src={IMG.suppliers} alt="Buyer suppliers directory" url={URL("/buyer/suppliers")} />,
    },
  ],
  faq: [
    { q: "Is this for any small-business owner?", a: "No — Verqo Buyer is for retail-shop owners sourcing physical goods (beauty, apparel, food, home, health, CPG). Not for service businesses or agency owners." },
    { q: "Does it work if I only have 3 suppliers?", a: "Yes — but the network effect is bigger with more suppliers. The moment you add a 4th or 5th supplier, the AP and terms centralization becomes a real time-saver." },
  ],
  related: [
    REL_ROLE("cfo",            "CFOs & Finance",     "For finance leaders at retailers/chains."),
    REL_ROLE("head-of-buying", "Head of Buying",     "For the person owning the buy."),
    REL("retailers", "For retailers", "The indie retailer playbook."),
  ],
  cta: commonCta(),
};

const cfoBuyer = {
  eyebrow: "By role · CFOs & Finance leaders at retailers/chains",
  title: "One AP layer for every wholesale supplier.",
  subtitle: `Every invoice, every due date, every extension request — in one ledger, one export, one workflow. ${AUDIENCE_LINE}`,
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.paymentsOverview} alt="Buyer payments — one AP dashboard for every supplier" url={URL("/buyer/payments")} />,
  features: {
    heading: "AP consolidation without an ERP migration.",
    sub: "Verqo sits above your accounting software and centralizes the wholesale AP that used to be spread across 40 supplier portals.",
    items: [
      { icon: LineChart,     title: "One aging report",              desc: "Every open invoice across every supplier, in one aging view — sortable by supplier, due bucket, or category." },
      { icon: Wallet,        title: "One auto-debit rule",            desc: "Set once, applies everywhere: auto-pay on due, or 3-day grace, or manual approve — never miss a discount, never eat a late fee." },
      { icon: BoxesIcon,     title: "PO → invoice matching",           desc: "3-way match: PO → receiving → invoice — done automatically. Discrepancies queued for finance approval." },
      { icon: Landmark,      title: "Portable Net-terms",              desc: "One credit application, terms across every supplier in the network — no more 60 separate credit apps a year." },
      { icon: DollarSign,    title: "Extension without a phone call",  desc: "Need Net-90 on a rough month? Request in-app, get approved based on your live trade score, extend without a supplier conversation." },
      { icon: ShieldCheck,   title: "Buyer-side insurance",             desc: "Dropship damage, delivery disputes, and return-to-vendor risks covered — your P&L doesn't wear the loss." },
    ],
  },
  blocks: [
    {
      eyebrow: "Ledger-clean",
      title: "Every wholesale dollar traceable, from PO to payment.",
      body: "Verqo's ledger integrates with QuickBooks, NetSuite, Xero, and Sage Intacct — line-level attribution, no summary journal-entry games.",
      visual: <BrowserFrame src={IMG.paymentsCredit} alt="Buyer credit line — utilization & aging" url={URL("/buyer/payments")} />,
    },
  ],
  faq: [
    { q: "Any CFO can use this?", a: "No — Verqo Buyer is for finance leaders at retailers, chains, marketplaces, and procurement teams sourcing physical goods in wholesale industries. Not for SaaS, professional-services, or IT procurement." },
    { q: "Does it replace our accounting system?", a: "No. It's the AP-consolidation layer that sits above QuickBooks / NetSuite / etc. and normalizes wholesale supplier data across your whole book." },
  ],
  related: [
    REL_ROLE("founder-owner",  "Founders & Owners",  "For independent retailers."),
    REL_ROLE("head-of-buying", "Head of Buying",     "For the person owning the buy."),
    REL("chains",              "For chains",         "Multi-location retail playbook."),
  ],
  cta: commonCta(),
};

const headOfBuying = {
  eyebrow: "By role · Head of Buying at retailers/chains/marketplaces",
  title: "The buying job, without the tab-switching.",
  subtitle: `Discover, negotiate, and place orders across every supplier — with real merchandising data behind the buy. ${AUDIENCE_LINE}`,
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.suppliers} alt="Buyer suppliers directory" url={URL("/buyer/suppliers")} />,
  features: {
    heading: "Everything a wholesale buyer needs to place the next PO.",
    sub: "A modern buying workspace — supplier discovery, RFQ, catalog, terms, and receiving in one login.",
    items: [
      { icon: Search,        title: "One directory",              desc: "Every verified supplier in your category — orderable in one click, no separate portals." },
      { icon: MessageSquare, title: "One RFQ to many suppliers",   desc: "Send one RFQ, get parallel bids back — no more 12 emails and 4 spreadsheets to compare quotes." },
      { icon: Layers,        title: "Multi-supplier cart",         desc: "One cart across every supplier, one PO, one delivery date — even for orders that ship from 8 warehouses." },
      { icon: TrendingUp,    title: "Category velocity data",       desc: "See which SKUs move in shops like yours — buy against real sell-through data, not last-year's plan." },
      { icon: Bot,           title: "AI reorder suggestions",       desc: "Verqo watches your sales channels and suggests reorder qty + timing — pre-filled POs one click away." },
      { icon: Package,       title: "Consolidated freight",         desc: "Combine parcels from multiple suppliers when it saves you money — automatic, at buy time." },
    ],
  },
  blocks: [
    {
      eyebrow: "For the person in the buy meetings",
      title: "Walk into every buy meeting with the numbers.",
      body: "Verqo Buyer gives you the velocity, margin, and sell-through data behind every SKU across every supplier — so you're arguing with data, not vibes.",
      visual: <BrowserFrame src={IMG.dashboard} alt="Buyer dashboard — category performance & buying insight" url={URL("/buyer")} />,
    },
  ],
  faq: [
    { q: "Is this only for buyers at big chains?", a: "No — indie retailers, multi-location chains, marketplaces, and procurement teams all use Verqo Buyer. But it's scoped to physical-goods wholesale, not services." },
    { q: "Does it replace our vendor-management tool?", a: "For wholesale, yes. You keep your ERP for GL and inventory; Verqo owns the vendor discovery + PO + terms + payment workflow." },
  ],
  related: [
    REL_ROLE("founder-owner",  "Founders & Owners",  "For independent retailers."),
    REL_ROLE("cfo",            "CFOs & Finance",     "For finance leaders."),
    REL_ROLE("ops-manager",    "Ops & Store Managers","For receiving and store ops."),
  ],
  cta: commonCta(),
};

const opsManager = {
  eyebrow: "By role · Ops & Store Managers at retailers/chains",
  title: "The reorder-cadence and receiving flow, un-broken.",
  subtitle: `One place to reorder every supplier, receive every shipment, and reconcile every invoice — with SLA counters and exceptions surfaced. ${AUDIENCE_LINE}`,
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.ordersAll} alt="Buyer orders — cross-supplier order queue" url={URL("/buyer/orders")} />,
  features: {
    heading: "The Wednesday-afternoon tools an ops manager actually opens.",
    sub: "Not a giant analytics dashboard — a real queue that tells you what to do next.",
    items: [
      { icon: BoxesIcon,     title: "Unified order queue",       desc: "Every open PO across every supplier, sorted by SLA proximity — hottest at top, actionable, not passive." },
      { icon: Truck,         title: "Receiving without paper",    desc: "Scan-to-receive against POs, exception flags fire automatically, disputes filed in one tap." },
      { icon: Clock,         title: "SLA counters everywhere",    desc: "Late shipment? SLA counter turns red before the customer complains. Escalation rules pre-set." },
      { icon: Bell,          title: "Alerts you can act on",       desc: "Slack + email + in-app — only the alerts that actually need a human, not spam." },
      { icon: Landmark,      title: "Terms visible on every PO",   desc: "Every order shows the term (Net-30/60/90) and due date next to it — no more surprise due-tomorrow invoices." },
      { icon: ShieldCheck,   title: "Damage claims, auto-filed",   desc: "Photo the damage, submit — Verqo files the claim with the carrier and the supplier and tracks payout." },
    ],
  },
  blocks: [
    {
      eyebrow: "For the ops team",
      title: "Fewer tabs. Fewer meetings. More receiving actually done.",
      body: "Verqo Buyer is the operational spine of a retail back office — from reorder to receive to reconcile — without stitching 4 tools together.",
      visual: <BrowserFrame src={IMG.ordersAction} alt="Buyer orders action queue — SLA-sorted" url={URL("/buyer/orders")} />,
    },
  ],
  faq: [
    { q: "Is this like ShipStation for buyers?", a: "In spirit yes — but on the receiving side. Every parcel, every freight shipment, every reconciliation across every supplier in one queue." },
    { q: "Any ops team?",                         a: "No — this is retail / wholesale-buying ops specifically. Not for warehouse ops on the outbound side or SaaS support ops." },
  ],
  related: [
    REL_ROLE("head-of-buying", "Head of Buying",     "For the person owning the buy."),
    REL_ROLE("cfo",            "CFOs & Finance",     "For finance leaders."),
  ],
  cta: commonCta(),
};

export const BUYER_SOLUTIONS = {
  retailers, chains, marketplaces, procurement,
  "industry/beauty":   industries.beauty,
  "industry/apparel":  industries.apparel,
  "industry/food":     industries.food,
  "industry/home":     industries.home,
  "industry/health":   industries.health,
  "industry/cpg":      industries.cpg,
  "role/founder-owner":   founderOwner,
  "role/cfo":             cfoBuyer,
  "role/head-of-buying":  headOfBuying,
  "role/ops-manager":     opsManager,
};
